import AppProviders from "src/contexts/AppProviders";
import { store, StoreContext } from "src/stores/store";
import { AppRouter } from "src/router";
import { FeatureFlagsProvider } from "./contexts/FeatureFlagsContext";
import { useAuth } from "./contexts/AuthContext";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CommandPaletteShortcut } from "./components/cdp-ai/CommandPaletteShortcut";
import { TemplateLibrary } from "./components/cdp-ai/TemplateLibrary";

import "react-querybuilder/dist/query-builder.css";
import "react-chatbot-kit/build/main.css";

import "./styles/index.scss";
import { AuthProvider } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";

// Public pages — loaded without Keycloak ever initializing
const UnsubscribePage = lazy(() => import("src/pages/unsubscribe"));
const EmailSenderVerifyPage = lazy(() => import("src/pages/email/senders/verify"));

/**
 * Routes that must be accessible without any authentication.
 * Matched against window.location.pathname before AuthProvider mounts.
 */
const PUBLIC_PATH_PREFIXES = ["/unsubscribe", "/email/senders/"];

const isPublicRoute = PUBLIC_PATH_PREFIXES.some((prefix) =>
  window.location.pathname.startsWith(prefix),
);

// ── Component منفصل عشان يستخدم useAuth جوا AuthProvider ──
const AppWithFlags = () => {
  const { organizations, user } = useAuth();

  const currentOrgId = (() => {
    try {
      const stored = localStorage.getItem('currentOrganization');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.id as string | undefined;
      }
      return organizations?.[0]?.id as string | undefined;
    } catch {
      return organizations?.[0]?.id as string | undefined;
    }
  })();

  return (
    <FeatureFlagsProvider
      organizationId={currentOrgId}
      userId={user?.id}
    >
      <CommandPaletteShortcut />
      <TemplateLibrary />
      <AppRouter />
    </FeatureFlagsProvider>
  );
};

export const App = () => {
  // Render public pages without initializing Keycloak at all.
  // Contacts who click unsubscribe links have no Keycloak account and
  // must never see the login screen.
  if (isPublicRoute) {
    return (
      <BrowserRouter>
        <Suspense fallback={<div />}>
          <Routes>
            <Route path="unsubscribe" element={<UnsubscribePage />} />
            <Route
              path="email/senders/:id/verify/:token"
              element={<EmailSenderVerifyPage />}
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  return (
    <AuthProvider>
      <CurrencyProvider>
        <StoreContext.Provider value={store}>
          <AppProviders>
            <AppWithFlags />
          </AppProviders>
        </StoreContext.Provider>
      </CurrencyProvider>
    </AuthProvider>
  );
};